import clsx from 'clsx';
import { Loader2, Check, Camera, Pencil, Palette, Video, Wallet, Download, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useT } from '@/lib/i18n';

export const Spinner = ({ className }: { className?: string }) => <Loader2 aria-hidden className={clsx('animate-spin', className ?? 'size-5')} />;

export const PageSpinner = () => (
  <div className="grid min-h-[50vh] place-items-center" role="status">
    <Spinner className="size-8 text-brand" />
  </div>
);

export function SectionTitle({ icon: Icon, children, extra }: { icon: LucideIcon; children: ReactNode; extra?: ReactNode }) {
  return (
    <h2 className="mt-7 mb-3 flex items-center gap-2.5 text-lg font-semibold tracking-tight">
      <Icon className="size-5 text-brand" aria-hidden />
      <span>{children}</span>
      {extra}
    </h2>
  );
}

const STEP_ICON: Record<string, LucideIcon> = { photos: Camera, details: Pencil, design: Palette, video: Video, pay: Wallet, done: Download };

export function Stepper({ steps, current }: { steps: string[]; current: string }) {
  const { t } = useT();
  const n = steps.indexOf(current) + 1;
  if (n < 1) return null;
  return (
    <ol className="relative mt-5 mb-1 flex justify-between" aria-label={t('progress', { n, total: steps.length })}>
      <span aria-hidden className="absolute inset-x-5 top-1/2 h-0.5 -translate-y-1/2 bg-line" />
      {steps.map((s, i) => {
        const Icon = i + 1 < n ? Check : (STEP_ICON[s] ?? Check);
        return (
          <li
            key={s}
            className={clsx(
              'relative grid size-10 place-items-center rounded-full border-[1.5px]',
              i + 1 < n && 'border-ok bg-ok text-white',
              i + 1 === n && 'border-brand bg-brand text-white ring-5 ring-brand-soft',
              i + 1 > n && 'border-line bg-white text-[#b4aea5]',
            )}
          >
            <Icon className="size-[18px]" aria-hidden />
          </li>
        );
      })}
    </ol>
  );
}

export function Alert({ children, tone = 'danger' }: { children: ReactNode; tone?: 'danger' | 'warn' | 'ok' }) {
  return (
    <p role="alert" className={clsx('rounded-xl px-3.5 py-2.5 text-sm', tone === 'danger' && 'bg-danger-soft text-danger', tone === 'warn' && 'bg-warn-soft', tone === 'ok' && 'bg-ok-soft text-[#0e5c53]')}>
      {children}
    </p>
  );
}

export function Empty({ icon: Icon, children }: { icon: LucideIcon; children: ReactNode }) {
  return (
    <div className="grid justify-items-center gap-1 rounded-[18px] border border-dashed border-[#d9d4cc] bg-white p-7 text-muted">
      <Icon className="size-8 text-[#c0b9ae]" aria-hidden />
      <p className="m-0">{children}</p>
    </div>
  );
}

export function StatusPill({ status }: { status: string }) {
  const { t } = useT();
  return (
    <span
      className={clsx(
        'inline-block justify-self-start rounded-full px-2.5 py-0.5 text-xs',
        ['paid', 'ready'].includes(status) && 'bg-ok-soft text-[#0e5c53]',
        status === 'in_production' && 'bg-[#fff0c2] text-[#6b5000]',
        status === 'pending_payment' && 'bg-[#e3edff] text-[#1d4185]',
        status === 'cancelled' && 'bg-danger-soft text-danger',
        status === 'draft' && 'bg-[#eee] text-ink-2',
      )}
    >
      {t(`st.${status}`)}
    </span>
  );
}
